/**
 * Chat Control Tools for the VoiceChat component
 * 
 * This file contains tools for controlling the chat session:
 * - stopChat: Gracefully end the current chat session
 */
import { toolRegistry, createMessage, type OpenAIToolSchema, ToolName } from './ToolRegistry';
import type { ToolContext, Message } from './ToolRegistry';
import { useLogger } from '~/composables/useLogger';

// --------------------------------------
// stopChat Tool
// --------------------------------------

/**
 * Interface for stop chat arguments
 */
export interface StopChatArgs {
  reason?: string;
}

/**
 * Interface for stop chat result
 */
export interface StopChatResult {
  success: boolean;
  message: string;
}

/**
 * OpenAI schema for stopChat tool
 */
const stopChatSchema: OpenAIToolSchema = {
  type: 'function',
  name: ToolName.STOP_CHAT,
  description: 'End the conversation and close the voice chat session. Use this when the task is complete or when the user wants to end the conversation.',
  parameters: {
    type: 'object',
    properties: {
      reason: {
        type: 'string',
        description: 'The reason for ending the chat session (e.g., "Task completed", "User requested")',
        examples: ['Task completed', 'User requested to end chat', 'All questions answered']
      }
    },
    required: []
  }
};

// --------------------------------------
// Chat Control Tools Registration
// --------------------------------------

/**
 * Type definition for the shutdown callback function
 */
export type ShutdownCallback = (reason: string, delayMs: number) => void;

/**
 * Register the stopChat tool
 * @param shutdownCallback Function to call when the chat should be stopped
 */
function registerStopChatTool(shutdownCallback: ShutdownCallback): void {
  toolRegistry.register({
    name: ToolName.STOP_CHAT,
    openAISchema: stopChatSchema,
    execute: async (args: StopChatArgs, context: ToolContext) => {
      const logger = useLogger();
      logger.info(`${ToolName.STOP_CHAT}: Tool execution started`);

      // Extract reason parameter with default
      const reason = args.reason || 'Task completed';
      // Use fixed 15 second delay
      const delayMs = 15000; // 15 seconds

      logger.info(`${ToolName.STOP_CHAT}: Ending chat with reason: ${reason}, delay: ${delayMs}ms`);

      // Add farewell message to conversation
      const farewellMessage = createMessage(
        "I'll end our conversation now. Thank you for chatting with me. Have a great day!"
      );
      context.conversation.push(farewellMessage);

      // Call the shutdown callback to initiate the shutdown process
      shutdownCallback(reason, delayMs);

      // Return result
      return {
        success: true,
        message: `Chat ending with reason: ${reason}`
      };
    },
    formatResult: () => null, // We add the message in execute
    getContinuationInstructions: () => {
      return "If you are not already in the process of doing so, provide a polite and appropriate closure to the conversation, making sure the user's needs have been met before ending.";
    }
  });
}

/**
 * Register all chat control tools with the registry
 * 
 * @param shutdownCallback The callback function to trigger when chat should be stopped
 */
export function registerChatControlTools(shutdownCallback: ShutdownCallback): void {
  // Register each tool individually
  registerStopChatTool(shutdownCallback);

  console.info('Chat control tools registered');
} 